import React, { useRef, useState, useEffect } from 'react';
import { Drawer, Button, Spin, Tooltip, message, Modal, Tour, Input } from 'antd';
import CancelRecurringRefillModal from 'components/CancelRecurringRefillModal';
import type { TourProps } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import PreviewDetail from 'pages/dashboard/TextVisitPage/Dashboard/components/PreviewDetail';
import Prescriptions from 'components/Prescriptions';
import ChatBox from 'components/ChatBox';
import { shouldUseKlarityUi, formatUTCTime } from 'utils/common';
import s from './s.module.less';
import { IPaymentStatus, TextVisitServiceType } from 'types/textVisit';
import useGetCaseDetail from 'hooks/textVisit/useGetCaseDetail';
import Calendar from 'assets/prospects/calendar.svg';
import { releaseCase, saveOrSubmitCase } from 'api/textVisit';
import { getTimeLeft, getRefillServiceTitle } from 'pages/dashboard/TextVisitPage/utils';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { FollowUpServiceTypeMap } from 'pages/dashboard/TextVisitPage/Dashboard/components/MyCaseCard';
import { getHasShowAcceptDrawerTour, getUserId, setHasShowAcceptDrawerTour } from 'utils/localstore';
import ChargeModal from 'pages/dashboard/ProspectsDetailPage/components/prosepectInfo/components/ChargeModal';
import { useBillingPolicy } from 'hooks/paymentSetup/useBillingPolicy';
import { useGetConnectAccountInfo } from 'hooks/useGetConnectAccountInfo';
import InvoiceModal from 'pages/dashboard/ProspectsDetailPage/components/prosepectInfo/components/InvoiceModal';
import ProviderSignature from 'components/ProviderSignature';
import { textVisitDecline } from 'api/provider';

interface TextVisitUnSubmittedDrawerProps {
    show: boolean;
    onClose: () => void;
    onSave?: () => void;
    onNext?: (data: { nextTextVisitUuid: string; inProgress: boolean }) => void;
    textVisitUuid?: string;
    fullyWithSaveFun?: boolean;
    // 只读模式：group practice 下从 prospects-detail 打开时，整个 drawer 不允许写操作
    readOnly?: boolean;
    overrideProviderId?: number | string;
}

const TextVisitUnSubmittedDrawer = ({
    show,
    onClose,
    textVisitUuid,
    onSave,
    onNext,
    fullyWithSaveFun,
    readOnly,
    overrideProviderId
}: TextVisitUnSubmittedDrawerProps) => {
    const [caseData, loading, fetchData] = useGetCaseDetail({ caseUuid: textVisitUuid, overrideProviderId });
    const [isChatOpen, setIsChatOpen] = useState(true);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState<'goNext' | 'returnHome'>('goNext');
    const [tourOpen, setTourOpen] = useState(false);
    const prescriptionsRef = useRef<{ getValues: () => any; getTreatmentPlanSectionRef: () => HTMLDivElement | null }>(
        null,
    );
    const chatBoxRef = useRef<HTMLDivElement>(null);
    const isShowKlarityUi = shouldUseKlarityUi();
    const disableWrite = !!readOnly;
    const navigate = useNavigate();
    const [connectAccountInfo] = useGetConnectAccountInfo();
    const [billingPolicy] = useBillingPolicy();
    const [showChargeModal, setShowChargeModal] = useState(false);
    const [showSendInvoiceModal, setShowSendInvoiceModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const isRefill =
        caseData?.serviceType === TextVisitServiceType.MEDICATION_REFILL ||
        caseData?.serviceType === TextVisitServiceType.PRESCRIPTION_REFILL;
    const [hasSignature, setHasSignature] = useState(false);
    const [showDeclineRefilleModal, setShowDeclineRefilleModal] = React.useState(false);
    const [declineRefilleReason, setDeclineRefilleReason] = React.useState('');
    const [sendingDeclineRefilleMessage, setSendingDeclineRefilleMessage] = React.useState(false);
    const [showCancelRecurringModal, setShowCancelRecurringModal] = React.useState(false);
    const isRecurringSeries = Boolean(caseData?.recurrenceId && caseData.recurrenceId > 0);

    useEffect(() => {
        if (show && textVisitUuid) {
            fetchData(textVisitUuid);
            // 检查是否需要显示 Tour
            if (!getHasShowAcceptDrawerTour()) {
                setTimeout(() => {
                    setTourOpen(true);
                }, 500);
            }
        }
    }, [show]);
    React.useEffect(() => {
        if (caseData) {
            setDeclineRefilleReason(
                `Hi ${caseData?.patientFirstName || caseData?.patientLastName}, I’m unable to fulfill this prescription refill at this time. Please book a visit so we can review your request and discuss next steps.`,
            );
        }
    }, [caseData]);
    /**
     * Keep consistent with the “Credit card pending / on file” badge on client profile.
     * The source of truth is `prospect.creditCardOnFile` from backend.
     */
    const creditCardOnFile = Boolean(caseData?.prospect?.creditCardOnFile);
    const isPrescriptionRefill = caseData?.serviceType === TextVisitServiceType.PRESCRIPTION_REFILL;
    const showDecline = isPrescriptionRefill && !caseData?.providerTreatmentSubmitted;
    const showChargeFlowAfterSubmit =
        caseData?.paymentStatus !== IPaymentStatus.SUCCEEDED && isPrescriptionRefill && !caseData?.hasSentInvoice;

    const canRelease = caseData ? dayjs().diff(dayjs.utc(caseData.assignedProviderAt).local(), 'minutes') <= 10 : false;
    const isFollowUpCase = caseData?.serviceType !== TextVisitServiceType.INITIAL_VISIT;
    const patientName = `${caseData?.patientFirstName} ${caseData?.patientLastName}`;

    const refillTitle = getRefillServiceTitle({
        serviceType: caseData?.serviceType,
        specialty: (caseData as any)?.specialty ?? null,
        assessmentName: caseData?.assessmentName ?? null,
    });

    // eslint-disable-next-line no-nested-ternary
    const caseTitle = caseData
        ? isFollowUpCase
            ? refillTitle || FollowUpServiceTypeMap[caseData.serviceType as keyof typeof FollowUpServiceTypeMap]?.title
            : caseData.assessmentName
        : '';

    const handleFlowAfterSubmit = (goNext: boolean, res: any) => {
        onSave?.();
        const prepaidInvoicePaid = res?.data?.data?.prepaidInvoicePaid;
        if (showChargeFlowAfterSubmit && !prepaidInvoicePaid) {
            setShowChargeModal(true);
            return;
        }
        if (fullyWithSaveFun && onSave) {
            onClose();
            return;
        }

        if (isFollowUpCase) {
            navigate('/dashboard/text-visit?tab=my');
            onClose();
        }
        if (goNext) {
            message.success('Visit Notes sent successfully.');
            onClose();
            // 等待 drawer 关闭后再调用 onNext
            setTimeout(() => {
                onNext?.(res.data.data);
            }, 100);
        } else {
            onClose();
        }
    };
    const handleSubmitOrSave = async (isSave: boolean, goNext: boolean) => {
        if (!caseData || submitting) {
            return;
        }
        const params = prescriptionsRef.current?.getValues();
        if (!params.treatmentPlan) {
            message.error('Please add a visit notes');
            return;
        }
        setSubmitting(true);
        try {
            const res = await saveOrSubmitCase(
                {
                    ...params,
                    textVisitId: caseData.id,
                    submit: !isSave,
                },
                caseData.assignedProviderId ?? undefined,
            );
            if (!res.error) {
                setShowConfirmModal(false);
                // Hotfix 2026-05-12: For prescription refill, the backend
                // orchestrates the charge inline (see TextVisitChargeOrchestrator).
                // No ChargeModal / SendInvoiceModal — refresh the list and close
                // the drawer (handleFlowAfterSubmit's own ChargeModal branch would
                // otherwise re-trigger ChargeModal because prepaidInvoicePaid stays
                // false for held refills).
                if (isPrescriptionRefill) {
                    if (res.data?.data?.noteHeld) {
                        message.info('Visit notes are held. Patient will be notified once payment is confirmed.');
                    } else if (!isSave) {
                        message.success('Visit Notes submitted.');
                    }
                    onSave?.();
                    onClose();
                    if (goNext) {
                        setTimeout(() => onNext?.(res.data.data), 100);
                    }
                    return;
                }

                // Non-refill: existing flow unchanged.
                // prepaidInvoicePaid=true means the recurrence prepaid invoice was already paid;
                // skip the charge flow entirely in that case.
                const prepaidInvoicePaid = res.data?.data?.prepaidInvoicePaid;
                if (showChargeFlowAfterSubmit && !prepaidInvoicePaid) {
                    // Refresh the list immediately after submit so the case state updates,
                    // even if the user still needs to complete the charge flow.
                    onSave?.();
                    if (creditCardOnFile) {
                        setShowChargeModal(true);
                    } else {
                        setShowSendInvoiceModal(true);
                    }
                    return;
                }
                handleFlowAfterSubmit(goNext, res);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleReleaseCase = async () => {
        if (!caseData) {
            return;
        }
        try {
            const res = await releaseCase(caseData.id, caseData.assignedProviderId ?? undefined);
            if (!res.error) {
                onClose?.();
                onSave?.();
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleDeclineRefill = () => {
        if (isRecurringSeries) {
            setShowCancelRecurringModal(true);
        } else {
            setShowDeclineRefilleModal(true);
        }
    };

    const handleDoDeclineRefille = async () => {
        setSendingDeclineRefilleMessage(true);
        const res = await textVisitDecline(caseData?.id!, declineRefilleReason);
        setSendingDeclineRefilleMessage(false);
        if (!res.error) {
            message.success('Refill request declined successfully');
            setShowDeclineRefilleModal(false);
            onClose?.();
            onSave?.();
        }
    };

    const steps: TourProps['steps'] = [
        {
            title: ' ',
            description: 'Use chat to ask the patient any follow-up questions or request additional information.',
            target: () => chatBoxRef.current || document.body,
            placement: 'left',
        },
        {
            title: ' ',
            description: 'Document clinical decisions and care details in the visit notes section.',
            target: () =>
                (prescriptionsRef.current?.getTreatmentPlanSectionRef &&
                    prescriptionsRef.current.getTreatmentPlanSectionRef()) ||
                document.body,
            placement: 'left',
        },
    ];

    const handleRefetchCaseData = () => {
        if (textVisitUuid) {
            fetchData(textVisitUuid);
        }
    };

    return (
        <>
            <Drawer
                open={show}
                width="95vw"
                onClose={onClose}
                placement="right"
                rootClassName={`${s.drawer} ${isShowKlarityUi ? s.klarityDrawer : ''}`}
                zIndex={900}
                title={
                    <div className={s.header}>
                        <div className="flex flex-col gap-1">
                            <span className="text-base font-medium">Text visit: {caseTitle}</span>
                            {caseData && (
                                <div className="flex items-center gap-2 mt-1 font-normal">
                                    <div className="flex items-center gap-1 text-sm text-gray-900">
                                        <img src={Calendar} alt="calendar" />
                                        <span>
                                            Submitted on {formatUTCTime(caseData.patientSubmitAt || '', 'MMM D')}
                                        </span>
                                    </div>
                                    {caseData.assignedProviderAt && !isRefill && (
                                        <div className={s.activeTag}>
                                            Active: {getTimeLeft(caseData.assignedProviderAt).text}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            {caseData?.payoutAmount && (
                                <div className={s.priceTag}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                    >
                                        <path
                                            d="M6.43338 5.41784C6.58818 5.31464 6.77939 5.2224 7 5.15101L7.00001 6.84899C6.77939 6.7776 6.58818 6.68536 6.43338 6.58216C6.06927 6.33942 6 6.1139 6 6C6 5.8861 6.06927 5.66058 6.43338 5.41784Z"
                                            fill="#10B981"
                                        />
                                        <path
                                            d="M9.00002 10.849L9.00001 9.15101C9.22062 9.2224 9.41184 9.31464 9.56663 9.41784C9.93075 9.66058 10 9.8861 10 10C10 10.1139 9.93075 10.3394 9.56664 10.5822C9.41184 10.6854 9.22063 10.7776 9.00002 10.849Z"
                                            fill="#10B981"
                                        />
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16ZM9 3C9 2.44772 8.55228 2 8 2C7.44772 2 7 2.44772 7 3V3.09199C6.3784 3.20873 5.80348 3.43407 5.32398 3.75374C4.6023 4.23485 4 5.00933 4 6C4 6.99067 4.6023 7.76515 5.32398 8.24626C5.80348 8.56593 6.37841 8.79128 7.00001 8.90801L7.00002 10.8492C6.60902 10.7223 6.31917 10.5319 6.15667 10.3446C5.79471 9.92748 5.16313 9.88274 4.74599 10.2447C4.32885 10.6067 4.28411 11.2382 4.64607 11.6554C5.20855 12.3036 6.05956 12.7308 7 12.9076L7 13C6.99999 13.5523 7.44769 14 7.99998 14C8.55226 14 8.99999 13.5523 9 13L9 12.908C9.62161 12.7913 10.1965 12.5659 10.676 12.2463C11.3977 11.7651 12 10.9907 12 10C12 9.00933 11.3977 8.23485 10.676 7.75373C10.1965 7.43407 9.6216 7.20873 9.00001 7.09199L9 5.15075C9.391 5.27771 9.68085 5.4681 9.84335 5.65538C10.2053 6.07252 10.8369 6.11726 11.254 5.7553C11.6712 5.39335 11.7159 4.76176 11.354 4.34462C10.7915 3.69637 9.94046 3.26915 9 3.09236V3Z"
                                            fill="#10B981"
                                        />
                                    </svg>
                                    {caseData.payoutAmount}
                                </div>
                            )}
                            <Button type="text" icon={<CloseOutlined />} onClick={onClose} className={s.closeBtn} />
                        </div>
                    </div>
                }
                closeIcon={null}
                footer={
                    !loading && caseData ? (
                        <div className={s.footer}>
                            {!isFollowUpCase && (
                                <Button
                                    type="primary"
                                    disabled={submitting || disableWrite}
                                    onClick={() => {
                                        setConfirmAction('goNext');
                                        setShowConfirmModal(true);
                                    }}
                                >
                                    Submit & open next case
                                </Button>
                            )}
                            <Button
                                onClick={() => {
                                    setConfirmAction('returnHome');
                                    setShowConfirmModal(true);
                                }}
                                type="primary"
                                disabled={submitting || disableWrite}
                            >
                                {!isFollowUpCase ? 'Submit & return home' : 'Submit'}
                            </Button>
                            {showDecline && (
                                <Button
                                    type="text"
                                    onClick={() => handleDeclineRefill()}
                                    className={s.textBtn}
                                    disabled={submitting || disableWrite}
                                >
                                    {isRecurringSeries ? 'Cancel refill' : 'Decline refill request'}
                                </Button>
                            )}
                            <Button
                                type="text"
                                onClick={() => handleSubmitOrSave(true, false)}
                                className={s.textBtn}
                                loading={submitting}
                                disabled={submitting || disableWrite}
                            >
                                Save and exit
                            </Button>
                            {!isFollowUpCase &&
                                (canRelease && !disableWrite ? (
                                    <Button type="text" onClick={handleReleaseCase} className={s.textBtn}>
                                        Release to others
                                    </Button>
                                ) : (
                                    <Tooltip title="Cases can only be released within 10 minutes of accepting">
                                        <Button type="text" onClick={handleReleaseCase} disabled>
                                            Release to others
                                        </Button>
                                    </Tooltip>
                                ))}
                        </div>
                    ) : null
                }
                destroyOnClose
            >
                <div className={s.loadingWrapper}>
                    <Spin spinning={loading} tip="Loading">
                        {caseData ? (
                            <div className={s.content}>
                                <div style={{ overflowY: 'auto' }}>
                                    <div className={s.previewDetail}>
                                        <PreviewDetail
                                            caseData={caseData}
                                            onChat={() => setIsChatOpen(true)}
                                            isAcceptMode
                                            isProviderEligible
                                            refetchCaseData={handleRefetchCaseData}
                                            showBillingForVisit
                                        />
                                    </div>
                                </div>
                                <div className={s.prescriptions}>
                                    <Prescriptions
                                        ref={prescriptionsRef}
                                        businessId={caseData.id}
                                        caseData={caseData}
                                        refetch={(nextCaseUuid) => fetchData(nextCaseUuid as string)}
                                        type="text"
                                    />
                                </div>
                                {isChatOpen && (
                                    <div ref={chatBoxRef} className={s.chatBox}>
                                        <ChatBox patientId={caseData.clientId} onClose={() => setIsChatOpen(false)} />
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div style={{ minHeight: 'calc(100vh - 208px)' }} />
                        )}
                    </Spin>
                </div>
                <Modal
                    open={showConfirmModal}
                    onCancel={() => setShowConfirmModal(false)}
                    footer={null}
                    closeIcon={null}
                    centered
                    className={s.confirmModal}
                    width={520}
                >
                    <div className={s.modalContent}>
                        <div className={s.modalIconWrapper}>
                            <div className={s.modalIcon}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <path
                                        d="M18 16.08C17.24 16.08 16.56 16.38 16.04 16.85L8.91 12.7C8.96 12.47 9 12.24 9 12C9 11.76 8.96 11.53 8.91 11.3L15.96 7.19C16.5 7.69 17.21 8 18 8C19.66 8 21 6.66 21 5C21 3.34 19.66 2 18 2C16.34 2 15 3.34 15 5C15 5.24 15.04 5.47 15.09 5.7L8.04 9.81C7.5 9.31 6.79 9 6 9C4.34 9 3 10.34 3 12C3 13.66 4.34 15 6 15C6.79 15 7.5 14.69 8.04 14.19L15.16 18.34C15.11 18.55 15.08 18.77 15.08 19C15.08 20.61 16.39 21.92 18 21.92C19.61 21.92 20.92 20.61 20.92 19C20.92 17.39 19.61 16.08 18 16.08Z"
                                        fill="#0284C7"
                                    />
                                </svg>
                            </div>
                        </div>
                        <div className={s.modalTextWrapper}>
                            <h3 className={s.modalTitle}>
                                {isPrescriptionRefill
                                    ? 'Submit visit notes & charge patient'
                                    : 'Submit visit notes to patient'}
                            </h3>
                            {isPrescriptionRefill ? (
                                <p className={s.modalDescription}>
                                    Please review before submitting. The visit notes will be{' '}
                                    <strong>signed and recorded under your name with a timestamp</strong>.
                                    <br />
                                    <br />
                                    We&apos;ll then automatically charge the patient{' '}
                                    <strong>
                                        $
                                        {caseData?.patientChargeAmount != null
                                            ? Number(caseData.patientChargeAmount).toFixed(2)
                                            : '—'}
                                    </strong>{' '}
                                    for the prescription refill service. The patient will see the visit notes after
                                    successful payment. If the charge fails, an invoice will be sent for them to pay.
                                </p>
                            ) : (
                                <p className={s.modalDescription}>
                                    Please review before submitting. After submission, the visit notes will be{' '}
                                    <strong>signed and sent to the patient immediately</strong>. It will also be
                                    recorded under your name with a timestamp.
                                </p>
                            )}
                            <ProviderSignature onSignatureStatusChange={setHasSignature} />
                        </div>
                        <div className={s.modalActions}>
                            <Button
                                key="cancel"
                                onClick={() => setShowConfirmModal(false)}
                                className={s.modalCancelBtn}
                                disabled={submitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                key="confirm"
                                type="primary"
                                loading={submitting}
                                disabled={submitting}
                                onClick={() => {
                                    if (!hasSignature) {
                                        message.warning('Please add your signature before submitting.');
                                        return;
                                    }
                                    handleSubmitOrSave(false, confirmAction === 'goNext');
                                }}
                                className={s.modalConfirmBtn}
                            >
                                {isPrescriptionRefill ? 'Sign & Submit' : 'Sign & Send'}
                            </Button>
                        </div>
                    </div>
                </Modal>
                <Tour
                    open={tourOpen}
                    onClose={() => {
                        setTourOpen(false);
                        setHasShowAcceptDrawerTour(true);
                    }}
                    steps={steps}
                    indicatorsRender={(current, total) => (
                        <span>
                            Step {current + 1} out of {total}
                        </span>
                    )}
                    className={s.tour}
                />
            </Drawer>

            <ChargeModal
                connectAccountInfo={connectAccountInfo}
                providerId={getUserId()}
                clientId={caseData?.clientId}
                billingPolicy={billingPolicy?.sort((a, b) => a.id - b.id)}
                open={showChargeModal}
                onCancel={() => {
                    setShowChargeModal(false);
                    setShowSendInvoiceModal(false);
                    onClose?.();
                }}
                type="prescription-refill"
                chargeWithSquareCard={creditCardOnFile}
                onSuccess={() => {
                    // Charge client successfully: refresh outer list then close drawer
                    onSave?.();
                    setShowChargeModal(false);
                    setShowSendInvoiceModal(false);
                    onClose?.();
                }}
                textVisitId={caseData?.id?.toString()}
            />
            <InvoiceModal
                open={showSendInvoiceModal}
                onCancel={() => {
                    setShowChargeModal(false);
                    setShowSendInvoiceModal(false);
                    onClose?.();
                }}
                onSuccess={() => {
                    onSave?.();
                    setShowChargeModal(false);
                    setShowSendInvoiceModal(false);
                    onClose?.();
                }}
                patientName={patientName.trim() || 'Patient'}
                clientId={caseData?.clientId}
                state={caseData?.state!}
                textVisitId={caseData?.textVisitUuid}
                sourceTextVisitId={caseData?.id}
            />
            <Modal
                okText="Send message"
                okButtonProps={{
                    loading: sendingDeclineRefilleMessage,
                }}
                open={showDeclineRefilleModal}
                title="Unable to fulfill prescription refill request"
                onOk={handleDoDeclineRefille}
                onCancel={() => {
                    setShowDeclineRefilleModal(false);
                }}
            >
                <div className={s.mWrap}>
                    <p>
                        You won’t be able to prescribe this medication for this patient. Please enter a reason and
                        notify the patient.
                    </p>
                    <Input.TextArea
                        rows={4}
                        value={declineRefilleReason}
                        onChange={(e) => setDeclineRefilleReason(e.target.value)}
                    />
                </div>
            </Modal>
            <CancelRecurringRefillModal
                open={showCancelRecurringModal}
                textVisitId={caseData?.id}
                patientName={patientName}
                onClose={() => setShowCancelRecurringModal(false)}
                onSuccess={() => {
                    onClose?.();
                    onSave?.();
                }}
            />
        </>
    );
};

export default TextVisitUnSubmittedDrawer;
